// Shared primitives ported from Claude Design handoff (Direction A: Sinal).
// Used by all landing sections.

import type { CSSProperties, ReactNode } from "react";

// ── Logo ──────────────────────────────────────────────────────────
// Direction A approved mark: rounded square with geometric two-color C.
// Optical nudge +2px on X keeps the C visually centered inside the square.

export const Wordmark = ({
  size = 20,
  tone = "ink",
}: {
  size?: number;
  tone?: "ink" | "accent";
}) => {
  const color = tone === "accent" ? "var(--cy-accent)" : "var(--cy-ink-100)";
  return (
    <span
      style={{
        fontFamily: "var(--cy-fs-sans)",
        fontSize: size,
        fontWeight: 500,
        letterSpacing: "-0.02em",
        color,
        display: "inline-flex",
        alignItems: "baseline",
        gap: 0,
      }}
    >
      Cyphron
    </span>
  );
};

export const LogoMark = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
    <rect
      x="2"
      y="2"
      width="28"
      height="28"
      rx="7"
      fill="none"
      stroke="var(--cy-accent)"
      strokeWidth="1.8"
    />
    <path
      d="M12 12 Q12 10 14 10 L20 10"
      stroke="var(--cy-accent)"
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M12 16 Q12 22 20 22"
      stroke="var(--cy-ink-100)"
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

export const Logo = ({
  size = 28,
  gap = 10,
  textSize = 19,
}: {
  size?: number;
  gap?: number;
  textSize?: number;
}) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap }}>
    <LogoMark size={size} />
    <Wordmark size={textSize} />
  </span>
);

// ── Icons ─────────────────────────────────────────────────────────

export const CheckMark = ({
  size = 16,
  color = "var(--cy-accent)",
}: {
  size?: number;
  color?: string;
}) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden>
    <path
      d="M3 8.5 L6.5 12 L13 5"
      stroke={color}
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const XMark = ({
  size = 16,
  color = "var(--cy-ink-40)",
}: {
  size?: number;
  color?: string;
}) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden>
    <path
      d="M4 4 L12 12 M12 4 L4 12"
      stroke={color}
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

export const ArrowIcon = ({
  size = 14,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden>
    <path
      d="M2 7 H12 M8 3 L12 7 L8 11"
      stroke={color}
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── TapeLabel (eyebrow with accent bullet) ────────────────────────

export const TapeLabel = ({
  children,
  color = "var(--cy-accent)",
}: {
  children: ReactNode;
  color?: string;
}) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: color,
        boxShadow: `0 0 10px ${color}`,
      }}
    />
    <span className="cy-tape" style={{ color: "var(--cy-ink-80)" }}>
      {children}
    </span>
  </div>
);

// ── AmbientGlow ───────────────────────────────────────────────────
// Low-intensity green radial gradients that slowly drift. Respects
// prefers-reduced-motion via [data-ambient] attribute targeted in CSS.

type AmbientVariant = "tl-br" | "tr-bl" | "bl" | "tr";

const ambientBlobs: Record<
  AmbientVariant,
  Array<{ top?: string; left?: string; right?: string; bottom?: string; anim: string; size: number }>
> = {
  "tl-br": [
    { top: "-12%", left: "-8%", anim: "cy-amb-a", size: 720 },
    { bottom: "-18%", right: "-12%", anim: "cy-amb-b", size: 780 },
  ],
  "tr-bl": [
    { top: "-12%", right: "-10%", anim: "cy-amb-b", size: 720 },
    { bottom: "-15%", left: "-12%", anim: "cy-amb-a", size: 780 },
  ],
  bl: [{ bottom: "-22%", left: "-15%", anim: "cy-amb-a", size: 860 }],
  tr: [{ top: "-18%", right: "-12%", anim: "cy-amb-b", size: 820 }],
};

export const AmbientGlow = ({
  variant = "tl-br",
  intensity = 1,
}: {
  variant?: AmbientVariant;
  intensity?: number;
}) => {
  const blobs = ambientBlobs[variant] ?? [];
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {blobs.map((b, i) => {
        const style: CSSProperties = {
          position: "absolute",
          width: b.size,
          height: b.size,
          top: b.top,
          left: b.left,
          right: b.right,
          bottom: b.bottom,
          background: `radial-gradient(circle, rgba(132,255,0,${
            0.18 * intensity
          }) 0%, rgba(132,255,0,${0.07 * intensity}) 28%, transparent 60%)`,
          filter: "blur(70px)",
          animation: `${b.anim} ${28 + i * 6}s ease-in-out infinite`,
          willChange: "transform, opacity",
        };
        return <div key={i} data-ambient style={style} />;
      })}
    </div>
  );
};

// ── AccentPill (badge with pulsing dot) ──────────────────────────

export const AccentPill = ({ children }: { children: ReactNode }) => (
  <span className="cy-pill">
    <span className="cy-dot" />
    {children}
  </span>
);
